import React, { useEffect, useState } from 'react';
import InfoAttendants from './InfoAttendants';
import InfoTimeline from './InfoTimeline';
import InfoDetails from './InfoDetails';
import Header from '../../../components/Header';
import { useNavigate, useParams } from 'react-router-dom';
import { getShiftLogDetails } from '../../../services/superAdminServices';
import { number } from 'yup';
import Loader from '../../../components/Loader/Loader';
import { AlertCircle, X } from 'lucide-react';
import Activities from '../../../components/Activities/Activity';
import { useProfile } from '../../../Contexts/ProfileContext';
import { isReadOnlyShiftLogRole } from '../../../constants/roles';

export interface UserType {
  id: number;
  code: string;
  title: string;
  created_at: string;
  updated_at: string;
}

export interface User {
  id: number;
  name: string;
  email: string;
  phone: string;
  profile_photo: string | null;
  user_type?: UserType | null;
}

export interface Attendee {
  id: number;
  shift_log_id: number;
  user_id: number;
  designation: string | null;
  user: User;
  created_at: string;
}

export interface ShiftData {
  id: number;
  reference_number: string;
  date: string;
  start_time: string;
  end_time: string;
  department: string | null;
  status: string;
  location: string;
  priority: string;
  created_by: string;
  description: string;
  unit_on_load: string;
  site_id?: number;
  site?: {
    id: number;
    name: string;
  } | null;
  created_at: string;
  attendees: Attendee[];
}

export default function ShiftInfoPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const profile: any = useProfile();
  const readOnly = isReadOnlyShiftLogRole(profile?.role);

  const [shiftLogsDetail, setShiftLogsDetail] = useState<ShiftData | null>(null);

  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState('');
  const [showError, setShowError] = useState(false);

  const fetchLogsDetails = async () => {
    try {
      if (!id) return;
      setLoading(true);

      //  Convert id string → number
      const numericId = Number(id);

      // Prevent NaN calls
      if (isNaN(numericId)) {
        console.error('Invalid ID in URL');
        setApiError('Invalid shift ID');
        setShowError(true);
        setTimeout(() => setShowError(false), 5000);
        return;
      }

      const data = await getShiftLogDetails(numericId);
      const shift = data?.data || null;

      if (readOnly && !shift?.attendees?.some((attendee: Attendee) => Number(attendee.user_id) === Number(profile?.id))) {
        setShiftLogsDetail(null);
        setApiError('You can only view shift logs where you were assigned as an attendant or assistant.');
        setShowError(true);
        return;
      }

      setShiftLogsDetail(shift);
    } catch (err: any) {
      console.error(err);
      const message = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to load shift details';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchLogsDetails();
  }, [id]);

  if (loading) return <Loader />;

  return (
    <>
      <Header title={'Shift Details'} showBackButton={true} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left Section */}
        <div className="md:col-span-2">
          {!shiftLogsDetail && !loading && (
            <div className="mt-6 rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
              This shift log is unavailable or is not assigned to you.
            </div>
          )}
          {shiftLogsDetail && <InfoDetails data={shiftLogsDetail} refreshData={fetchLogsDetails} readOnly={readOnly} />}

          {shiftLogsDetail && (
            <InfoTimeline
              shiftLogId={shiftLogsDetail.id}
              isSignedOff={shiftLogsDetail.status?.toLowerCase() === 'signed off'}
              readOnly={readOnly}
            />
          )}
        </div>

        {/* Right Section */}
        <div className="flex flex-col gap-6">
          {shiftLogsDetail && <InfoAttendants attendees={shiftLogsDetail.attendees} />}

          <Activities moduleName="shift_logs" moduleItemId={Number(id)} />
        </div>
      </div>

      {/* Error Notification */}
      {showError && (
        <div className="fixed bottom-6 right-6 z-[100]">
          <div className="bg-red-500 text-white rounded-lg shadow-lg p-4 pr-12 min-w-[300px] max-w-[500px] relative animate-slide-in">
            <button onClick={() => setShowError(false)} className="absolute top-3 right-3 text-white hover:text-gray-200 transition-colors">
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold mb-1">Error</h4>
                <p className="text-sm">{apiError}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
